import { useEffect, useState, useRef } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Button, Skeleton,
  Tooltip,
  Box,
  Typography
} from "@mui/material";
import { getFixcenterProductByCategoryId, getProductsPricingDetails } from "../../services/products.service";
import { IProductPricingDetail } from "../../shared/models/product-pricing-detail.interface";
import { useParams } from "react-router-dom";
import { FixcenterProduct } from "../../shared/models/fixcenter-product.model";
import { IFixcenterProductDto } from "../../shared/models/fixcenter-product-dto.interface";
import { useTranslation } from "../../i18n/useTranslation";
import { Utils } from "../../shared/constants/utilities.constants";
import { claimIsActive } from "../../shared/services/claims.service";
import MarkAsFavoriteControl from "../../containers/favorite-products/mark-as-favorite/mark-as-favorite-control";
import { ErpClaimCode } from "../../shared/models/erp-claim-code.enum";
import { getCurrencySymbol } from "../order/services/currency.service";
import { useBetween } from "use-between";
import { PermissionsUseBetween } from "../../shared/use-between/permissions-use-between";
import { calculateTotalPricePerProduct, getNetPriceWithPrecision, getPriceWithPrecision } from "../shopping-cart/services/shopping-cart.service";
import QuantityInput from "../../shared/input/quantity-input ";
import WarningIcon from '@mui/icons-material/Warning';
import AddToShoppingCart from "./add-to-shopping-cart";
import styles from './FixcenterOrderingTable.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { determineIfRedirectOrDownload, getFileByUuid, getFileMapByUuids } from "../../shared/services/attachments.service";
import { PDF_EXTENSION, RFA_EXTENSION } from "../../shared/constants/file-extension.constants";
import { AttachmentFile } from "../../shared/models/attachment-file.model";
import { faFile } from '@fortawesome/free-solid-svg-icons';
import React from "react";

interface QuantityState {
    [productId: string]: number;
}

interface WarningState {
    [productId: string]: string;
}

export default function FixcenterOrderingTable(props: any) {
    const [products, setProducts] = useState<FixcenterProduct[]>([]);
    const [pricing, setPricing] = useState<Record<string, IProductPricingDetail>>({});
    const [visibleCount, setVisibleCount] = useState(20);
    const [loading, setLoading] = useState(false);
    const loaderRef = useRef<HTMLDivElement | null>(null);
    const { categoryId } = useParams();
    const { productId } = useParams();
    const { t } = useTranslation('translation');
    const { claims } = useBetween(PermissionsUseBetween);
    const [quantities, setQuantities] = useState<QuantityState>({});
    const [warnings, setWarnings] = useState<WarningState>({});
    const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        (async () => {
        const response = await getFixcenterProductByCategoryId(productId as string, categoryId as string);
        const dto: IFixcenterProductDto = response.data;
        setQuantities(Object.fromEntries(dto.data.map((product: FixcenterProduct) => [product.id, getMinQuantity(product)])));
        setWarnings(Object.fromEntries(dto.data.map((product: FixcenterProduct) => [product.id, ''])));
        setProducts(dto.data);
        props.handleSetProducts(dto.data);
        props.handleSetViewKeyValueProducts(dto.viewItemKeyValue);
        })();
    }, []);

    const loadPrices = async (start: number, end: number) => {
        const batch = products.slice(start, end);
        if (batch.length === 0) return;

        const uuids = batch.map((p) => p.id);

        const batchSize = 5;
        const chunks = [];
        for (let i = 0; i < uuids.length; i += batchSize) {
            chunks.push(uuids.slice(i, i + batchSize));
        }

        await Promise.all(
                chunks.map(async (chunk) => {
                const response = await getProductsPricingDetails({ productUuids: chunk });
                setPricing((prev) => ({ ...prev, ...response.data }));
            })
        );
    };

    useEffect(() => {
        if (products.length > 0) {
            setLoading(true);
            loadPrices(0, visibleCount).finally(() => setLoading(false));
        }
    }, [products]);

    useEffect(() => {
        if (!products.length) return;

        let isFetching = false;

       let scrollTimeout: NodeJS.Timeout | null = null;

        const handleScroll = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const container = document.querySelector(".product-table-container");
                if (!container || isFetching) return;

                const scrollPosition = container.scrollTop + container.clientHeight;
                const triggerPoint = container.scrollHeight * 0.85;

                if (scrollPosition >= triggerPoint && visibleCount < products.length) {
                    isFetching = true;
                    const newCount = Math.min(visibleCount + 20, products.length);
                    setVisibleCount(newCount);
                    loadPrices(visibleCount, newCount).finally(() => {
                        isFetching = false;
                    });
                }
            }, 100);
        };

        const container = document.querySelector(".product-table-container");
        container?.addEventListener("scroll", handleScroll);

        return () => container?.removeEventListener("scroll", handleScroll);
    }, [visibleCount, products]);

    const getMinQuantity = (product: FixcenterProduct): number => {
        return !Utils.isNullOrUndefined(product.salePackagingUnits) && product.salePackagingUnits.length > 0 ? product.salePackagingUnits[0].minQty : 1;
    };

    const calculateUnitPrice = (productPrice: IProductPricingDetail): number => {
        return productPrice.priceFromPricing * (1 - productPrice.discount)
    };

    const handleQuantityChange = (newQty: number, product: FixcenterProduct) => {
        const minQuantity: number = getMinQuantity(product);
        if (newQty < minQuantity) {
            setWarnings(prevWarnings => ({
                    ...prevWarnings,
                [product.id]: 'commonErrors.MIN_QUANTITY'
            }));
        } else if (newQty % minQuantity) {
                setWarnings(prevWarnings => ({
                    ...prevWarnings,
                [product.id]: 'commonErrors.NOT_MATCHING_PACKAKING_SIZE'
            }));
        } else {
                setWarnings(prevWarnings => ({
                    ...prevWarnings,
                [product.id]: ''
            }));
        }
        setQuantities(prevQuantities => ({
            ...prevQuantities,
            [product.id]: newQty
        }));
    };

     const fetchFileMap = async (product: FixcenterProduct) => {
        if (Array.isArray(product.attachments)) {
            toggleRowExpansion(product.id);
            return;
        }
        if (Array.isArray(product.attachmentIds) && product.attachmentIds.length > 0) {
            getFileMapByUuids(product.attachmentIds).then((response: any) => {
                if (response) {
                    const _fileMap = response.data as Record<string, string>;
                    if (!Array.isArray(product.attachments)) {
                        product.attachments = [];
                    }
                    for (const[key, value] of Object.entries(_fileMap)) {
                        if (value.endsWith(RFA_EXTENSION) || value.endsWith(PDF_EXTENSION)) {
                            product.attachments.push({id: key, name: value} as AttachmentFile);
                        }
                    }
                    toggleRowExpansion(product.id);
                }
            });            
        }
    };

    const getFileDocument = (fileDocumentUuid: string) => {
        getFileByUuid(fileDocumentUuid as string).then((response) => {
            if (response) {
                const model: AttachmentFile = response.data;
                if (model) {
                    determineIfRedirectOrDownload(model);
                }
            }
        });
    }

    const openDocuments = (product: FixcenterProduct) => {
        fetchFileMap(product);
    };

    const toggleRowExpansion = (productUuid: string) => {
        setExpandedRows(prevState => ({
            ...prevState,
            [productUuid]: !prevState[productUuid]
        }));
    };
    
    return (
        <div>
            <Typography variant="body2" sx={{ mb: 2 }}>
                {t('myAccountInfo.orders.MESSAGE_1')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
                {t('myAccountInfo.orders.MESSAGE_2')}
            </Typography>
            <Box sx={{display: 'flex'}}>
                <TableContainer
                    className="product-table-container"
                    component={Paper}
                    sx={{
                        maxHeight: "80vh",
                        borderRadius: 2,
                        boxShadow: 3,
                        overflowY: "auto",
                        margin: '1rem 1rem 1rem 0rem',
                        width: '100%'
                    }}
                >
                <Table sx={{width: '100%', tableLayout: 'auto'}} stickyHeader className={styles.table_container}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center"></TableCell>
                            <TableCell><b>{t('VARIANT_NAME')}</b></TableCell>
                            <TableCell><b>{t('CODE')}</b></TableCell>
                            <TableCell><b>{t('DOCUMENTS')}</b></TableCell>
                            <TableCell><b>{t('PACKAGING')}</b></TableCell>
                            <TableCell><b>{t('MIN_QUANTITY')}</b></TableCell>
                            {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) &&
                                <TableCell>
                                    <b>{t('NET_PRICE')}</b>
                                </TableCell>
                            }
                            <TableCell><b>{t('QUANTITY')}</b></TableCell>
                            {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) &&
                                <TableCell>
                                    <b>{t('TOTAL_PRICE')}</b>
                                </TableCell>
                            }
                            <TableCell align="center"></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                    {products.slice(0, visibleCount).map((product) => {
                        const price = pricing[product.id];
                        const isLoaded = !!price;

                        return (
                        <React.Fragment key={product.id}>
                        <TableRow hover>
                            <TableCell>
                                <MarkAsFavoriteControl categoryUuid={categoryId} parentProductUuid={productId} variantProductUuid={product.id} isActiveInit={product.isFavorite} ></MarkAsFavoriteControl>
                            </TableCell>
                            <TableCell>{product.translateName ?? product.name}</TableCell>
                            <TableCell>{product.code}</TableCell>
                            <TableCell align="center"> 
                                {product.attachmentIds && product.attachmentIds?.length > 0 &&
                                    <FontAwesomeIcon className={styles.redIcon} size="2x" onClick={() => { openDocuments(product) }} icon={faFile} />
                                }
                            </TableCell>
                            <TableCell>{!Utils.isNullOrUndefined(product.salePackagingUnits) && product.salePackagingUnits.length > 0 ? product.salePackagingUnits[0].packaging : ''}</TableCell>
                            <TableCell>{getMinQuantity(product)}</TableCell>
                            {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) &&
                                <TableCell>
                                {isLoaded ? (`${getNetPriceWithPrecision(calculateUnitPrice(price))} ${getCurrencySymbol(product.saleCurrencyName)}`) : (
                                    <Skeleton variant="text" width={60} />
                                )}
                                </TableCell>
                            }
                            <TableCell>
                                <section className={styles.quantity_section}>
                                <QuantityInput
                                        key={product.id}
                                        quantity={quantities[product.id] ?? 1}
                                        minQuantity={getMinQuantity(product)}
                                        onChange={(newQty) => handleQuantityChange(newQty, product)}
                                    />
                                    { warnings[product.id].length > 0 && <Tooltip sx={{zIndex: 1}} title={t(warnings[product.id])}>
                                        <WarningIcon></WarningIcon>
                                    </Tooltip>}
                                </section>
                            </TableCell>
                            {claimIsActive(claims, ErpClaimCode.CUSTOMER_VIEWP_ROLE) &&
                                <TableCell>
                                        <TableCell>
                                            {isLoaded ? (` ${calculateTotalPricePerProduct(calculateUnitPrice(price), (quantities[product.id] || 1), warnings[product.id].length > 0)} ${getCurrencySymbol(product.saleCurrencyName)}`) : (
                                                <Skeleton variant="text" width={60} />
                                            )}
                                        </TableCell>
                                
                                </TableCell>
                            }
                            <TableCell align="center">
                                {isLoaded ?
                                    <AddToShoppingCart
                                        product={product} 
                                        minQuantity={getMinQuantity(product)} 
                                        quantity={quantities[product.id]} 
                                        productParentUuid={productId} 
                                        netPrice={calculateUnitPrice(price)}
                                        brutPrice={price.priceFromPricing}
                                        discount={price.discount}
                                        packaging={!Utils.isNullOrUndefined(product.salePackagingUnits) && product.salePackagingUnits.length > 0 ? product.salePackagingUnits[0].packaging : null}
                                        isDisabled={warnings[product.id].length > 0 || !isLoaded}></AddToShoppingCart> : (
                                    <Skeleton variant="text" width={60} />
                                )}
                            </TableCell>
                        </TableRow>
                        {expandedRows[product.id] && product.attachments && product.attachments?.length > 0 && <TableRow>
                            <TableCell
                                colSpan={10}
                                sx={{
                                    p: 0,
                                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        backgroundColor: "#fafafa",
                                        px: 3,
                                        py: 2,
                                    }}
                                >
                                
                                    {product.attachments.map((attachment, index) => (
                                        <div className={styles.pdf_link} onClick={() => { getFileDocument(attachment.id) }} key={index}>{attachment.name}</div>
                                    ))}
                            
                                </Box>
                            </TableCell>
                        </TableRow>
                        }
                        </React.Fragment>
                        );
                    })}

                    {loading && (
                        <TableRow>
                        <TableCell colSpan={6} align="center">
                            <Skeleton variant="rectangular" width="80%" height={20} sx={{ mx: "auto" }} />
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
                <div ref={loaderRef} style={{ height: "20px" }} />
                </TableContainer>
            </Box>
        </div>
    );
}